/**
 * Data for the parties screens.
 *
 * The hooks only assemble; the decisions live in tested functions. `partyReader`
 * owns the company filter, and `useParties` never re-implements it — a screen
 * that filtered for itself is how one company's customers end up under another
 * company's GSTIN.
 */
import { useQuery } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import { listParties, type ListPartiesOptions } from '@data/services/partyReader';
import { useActiveBusiness } from '@features/documents/useDocuments';
import type { Party, PartyKind } from '@data/db/types';

/** Root of every party query, so a company switch invalidates the whole family. */
export const PARTIES_QUERY_ROOT = ['parties'] as const;

export interface UsePartiesOptions extends ListPartiesOptions {
  readonly kind?: PartyKind;
}

/**
 * The parties of the company currently open.
 *
 * The resolved business id is part of the query KEY, not just an argument
 * inside `queryFn`. That distinction is the whole reason this is safe: a key
 * that omits the company means company B's switch can be served company A's
 * cached customers for up to `staleTime`, which is precisely the bug the
 * document list shipped. The id comes from `useActiveBusiness()` so it is
 * available synchronously — reading it inside `queryFn` would put it in the
 * result but not the key, and the cache would lie.
 *
 * Returns `[]` before a company is chosen rather than throwing: an empty
 * customer picker is a first-run state, not a failure, and a screen that
 * renders an error there is noisier than one that says "no customers yet".
 */
export function useParties(options: UsePartiesOptions = {}) {
  const { kind, activeOnly = false, search = '', limit } = options;
  const business = useActiveBusiness();
  const businessId = business.data ?? null;

  return useQuery({
    queryKey: [
      ...PARTIES_QUERY_ROOT,
      'list',
      businessId,
      kind ?? 'ALL',
      activeOnly,
      search,
      limit ?? null,
    ],
    queryFn: async () => {
      // `businessId` is non-null whenever this runs: the query is disabled until
      // it resolves, so there is no re-read and no possibility of the two
      // disagreeing.
      if (businessId === null) return [] as Party[];
      return listParties(getDb() as never, businessId, { kind, activeOnly, search, limit });
    },
    enabled: businessId !== null,
  });
}

